import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/core/app.state";
import { BrandsService } from "../services/brands.services";
import { CommonService } from "src/app/shared/common.service";
import { catchError, finalize, map, of, switchMap, tap } from "rxjs";
import * as brandsActions from './brands.actions'
import { APIResponse } from "src/app/core/models/api-response";
import { IBrand } from "../models/brands.models";
import { AlertEnums } from "src/app/shared/enums/shared.enums";
import { TranslateService } from "@ngx-translate/core";
import { DEFAULT_PAGE_SIZE } from "src/app/config/global.config";

@Injectable()
export class BrandsEffects {
  constructor(
    private actions$: Actions,
    public store: Store<AppState>,
    private brandsServices: BrandsService,
    private commonService: CommonService,
    private translateService: TranslateService
  ) { }

  // fetch All Brands
  FetchAllBrandsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(brandsActions.FetchAllBrands),
      tap(() => {
        this.store.dispatch(brandsActions.SetBrandsLoader({ isLoading: true }));
      }),
      switchMap(({ includes }) =>
        this.brandsServices.listAllBrands(includes).pipe(
          map((res: APIResponse<IBrand>) => brandsActions.FetchAllBrandsSuccess({ brands: res.data })),
          catchError(() => of(
            brandsActions.FetchAllBrandsError()
          )),
          finalize(() => {
            this.store.dispatch(brandsActions.SetBrandsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // fetch Brands
  FetchBrandsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(brandsActions.FetchBrands),
      tap(() => {
        this.store.dispatch(brandsActions.SetBrandsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.brandsServices.listBrands(action.page, action.key).pipe(
          map((res: APIResponse<IBrand>) =>
            brandsActions.FetchBrandsSuccess({ brands: res, append: action.append })
          ),
          catchError(() => of(
            brandsActions.FetchBrandsError()
          )),
          finalize(() => {
            this.store.dispatch(brandsActions.SetBrandsLoader({ isLoading: false }));
          })
        )
      ),
    )
  );

  // add Brand
  AddBrandEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(brandsActions.AddBrand),
      tap(() => {
        this.store.dispatch(brandsActions.SetBrandsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.brandsServices.addBrand(action.brand).pipe(
          map((res: APIResponse<IBrand>) => brandsActions.AddBrandSuccess({ brand: res.data[0] })),
          tap(() => {
            this.store.dispatch(brandsActions.FetchBrands({ page: { number: 1, size: DEFAULT_PAGE_SIZE } }));
            this.commonService.showToast(
              this.translateService.instant('alerts.' + AlertEnums.brandAddSuccess),
              'SUCCESS'
            );
          }),
          catchError(() => of(brandsActions.AddBrandError())),
          finalize(() => {
            this.store.dispatch(brandsActions.SetBrandsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

    // update Brand
    UpdateBrandEffect$ = createEffect(() =>
      this.actions$.pipe(
        ofType(brandsActions.UpdateBrand),
        tap(() => {
          this.store.dispatch(brandsActions.SetBrandsLoader({ isLoading: true }));
        }),
        switchMap((action) =>
          this.brandsServices.updateBrand(action.id,action.brand).pipe(
            map((res: APIResponse<IBrand>) => brandsActions.UpdateBrandSuccess({ brand: res.data[0] })),
            tap(() => {
              this.store.dispatch(brandsActions.FetchBrands({ page: { number: 1, size: DEFAULT_PAGE_SIZE } }));
              this.commonService.showToast(
              this.translateService.instant('alerts.' + AlertEnums.brandUpdateSuccess),
              'SUCCESS'
            );
          }),
          catchError(() => of(brandsActions.UpdateBrandError())),
          finalize(() => {
            this.store.dispatch(brandsActions.SetBrandsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // delete Brand
  DeleteBrandEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(brandsActions.DeleteBrand),
      tap(() => {
        this.store.dispatch(brandsActions.SetBrandsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.brandsServices.deleteBrand(action.id).pipe(
          map((res: APIResponse<IBrand>) => brandsActions.DeleteBrandSuccess()),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant('alerts.' + AlertEnums.brandDeleteSuccess),
              'SUCCESS'
            );
          }),
          catchError(() => of(brandsActions.DeleteBrandError())),
          finalize(() => {
            this.store.dispatch(brandsActions.SetBrandsLoader({ isLoading: false }));
          })
        )
      )
    )
  );
}
